import { useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { BotaoFechar } from '@/componentes/Botao';
import { dispensar, useRetornos, type AcaoDoRetorno, type Retorno } from '@/lib/retorno';

import { efeitoEmTexto, linhaDoRegistro, TOM_DO_RETORNO, useLeiturasSaemComATela, usePausaDoRetorno } from './tomDoRetorno';
import { useNaCamadaDeCima } from '@/lib/camada';

/**
 * O TOAST (v2.1, p.31 e p.32, revisto em R04) — o que aconteceu na última
 * chamada, inteiro: "Todo toast diz o que foi feito, em qual registro, com
 * quais valores e o que isso mudou. A pessoa não precisa abrir a tela para
 * saber se deu certo."
 *
 * A anatomia, na ordem da p.32:
 *
 * 1. bloco do ícone no tom (verde fez, âmbar com ressalva, vermelho não fez,
 *    azul em andamento);
 * 2. o título — objeto e particípio;
 * 3. em quê — o código em monoespaçada e o nome;
 * 4. o que valeu — até três valores, separados por ponto médio;
 * 5. o que mudou além — no bloco rebaixado, com antes → depois;
 * 6. registro, hora e autor, no rodapé, com as ações;
 * 7. ações — "Ver" abre o registro. **Sem "Desfazer" na mesa**: o livro é só
 *    de inserção (decisões 36 a 38, ADR-008); no celular há (M32);
 * 8. a barra de 5 s, que pausa com o mouse em cima, o dedo ou o foco.
 *
 * O lugar é fixo: canto inferior direito, a 24 px das bordas; 360 px, raio
 * 16 e elevação 3. Até três ao mesmo tempo, o mais novo embaixo. O que não é
 * falha some em 5 s; a falha fica até ser fechada (a regra mora em
 * `lib/retorno`). Entra subindo 12 px (`--mov-entrada`); com redução de
 * movimento, só esmaece.
 *
 * A REGRA CONTINUA NA CAMADA DE CHAMADAS (ver `lib/retorno` e o H21): toda
 * escrita declara o seu retorno, e toda leitura que falha avisa. O toast só
 * desenha.
 */
export function Toasts() {
  const retornos = useRetornos();
  const pilha = useRef<HTMLDivElement>(null);
  useNaCamadaDeCima(pilha, retornos.at(-1)?.id);
  /* A FALHA DE LEITURA E DA TELA (item 12 dos robos): trocar de tela tira as
     da tela que ficou para tras — elas nao somem sozinhas, e empilhavam sobre
     o painel da tela seguinte. O retorno de gravacao fica. */
  useLeiturasSaemComATela();

  /* UMA pilha, na ordem de chegada (p.31: "até três, o mais novo embaixo").
     A diferença de urgência fica no anúncio: o que deu certo entra na região
     educada da pilha, e a falha leva `role="alert"`, que o leitor de tela diz
     na hora (`assertive`). */
  return (
    <div
      ref={pilha}
      data-camada-de-cima=""
      aria-live="polite"
      className="pilha-de-toasts pointer-events-none fixed right-6 bottom-6 z-[var(--z-toast)] flex w-[var(--toast-largura)] max-w-[calc(100vw-var(--esp-12))] flex-col gap-2 max-[768px]:inset-x-4 max-[768px]:bottom-4 max-[768px]:w-auto max-[768px]:max-w-none"
    >
      {retornos.map((r) => (
        <Toast key={r.id} retorno={r} />
      ))}
    </div>
  );
}

function Toast({ retorno: r }: { readonly retorno: Retorno }) {
  const tom = TOM_DO_RETORNO[r.tom];
  const { pausado, eventos } = usePausaDoRetorno(r);
  const navegar = useNavigate();
  const falha = r.tom === 'falha';
  const registro = linhaDoRegistro(r, r.tom === 'andamento' ? 'ainda não enviado' : undefined);
  const acoes = (r.acoes ?? []).filter((a) => a.rota !== undefined || a.aoTocar !== undefined);
  const tocar = (a: AcaoDoRetorno) => {
    if (a.aoTocar) a.aoTocar();
    else if (a.rota) void navegar({ to: a.rota } as never);
    dispensar(r.id);
  };
  const temRodape = registro !== '' || acoes.length > 0 || r.nota !== undefined;

  return (
    <div
      {...(falha ? { role: 'alert' } : {})}
      {...eventos}
      className="flutuante anima-toast pointer-events-auto overflow-hidden"
    >
      <div className="flex flex-col gap-2 px-4 pt-3 pb-1">
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className={`flex size-8 shrink-0 items-center justify-center rounded-controle ${tom.bloco}`}>
            <tom.Icone className={`size-[var(--icone-card)] ${tom.icone}`} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            {/* A situação dita por palavra, para o leitor de tela, antes do título. */}
            <span className="sr-only">{tom.falado}</span>
            <p className="txt-botao min-w-0 text-pretty text-tinta">{r.titulo}</p>
            {r.alvo ? (
              <p className="txt-apoio min-w-0 truncate text-tinta-secundaria">
                {r.alvo.codigo ? <span className="txt-codigo text-tinta">{r.alvo.codigo}</span> : null}
                {r.alvo.codigo && r.alvo.nome ? ' · ' : null}
                {r.alvo.nome ?? null}
              </p>
            ) : null}
          </div>
          <BotaoFechar
            rotulo={falha ? 'Fechar o aviso de falha' : 'Fechar o aviso'}
            aoFechar={() => {
              dispensar(r.id);
            }}
          />
        </div>

        {r.fatos && r.fatos.length > 0 ? (
          <p className="txt-apoio ml-11 text-pretty text-tinta-secundaria">{r.fatos.join(' · ')}</p>
        ) : null}

        {r.comoResolver ? <p className="txt-apoio ml-11 text-pretty text-tinta">{r.comoResolver}</p> : null}

        {r.efeito !== undefined ? <Efeito efeito={r.efeito} /> : null}

        {temRodape ? (
          <div className="ml-11 flex min-h-10 items-center gap-3 border-t border-borda-sutil">
            <span className="txt-legenda min-w-0 flex-1 truncate text-tinta-apagada">
              {r.nota ?? registro}
              {r.nota && r.contagem ? <span className="numero"> · {r.contagem}</span> : null}
            </span>
            {acoes.map((a) => (
              <button
                key={a.rotulo}
                type="button"
                onClick={() => {
                  tocar(a);
                }}
                className="txt-botao shrink-0 rounded-etiqueta px-1 text-marca-texto hover:underline"
              >
                {a.rotulo}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* A barra dos 5 s (p.32, 8), na cor do tom. A falha não conta: fica. */}
      {r.duracaoMs === null ? null : (
        <div aria-hidden="true" className="h-[3px] bg-borda-sutil">
          <div
            className={`barra-do-retorno h-full ${tom.barra}`}
            data-pausado={pausado ? '' : undefined}
            style={{ animationDuration: `${String(r.duracaoMs)}ms` }}
          />
        </div>
      )}
    </div>
  );
}

/** O que mudou além (p.32, 5): o bloco rebaixado, com antes → depois. */
function Efeito({ efeito }: { readonly efeito: NonNullable<Retorno['efeito']> }) {
  if (typeof efeito === 'string' || efeito.depois === undefined) {
    return <p className="txt-apoio ml-11 rounded-etiqueta bg-rebaixada px-3 py-2 text-pretty text-tinta">{efeitoEmTexto(efeito)}</p>;
  }
  return (
    <p className="txt-apoio ml-11 flex items-center gap-2 rounded-etiqueta bg-rebaixada px-3 py-2 text-tinta">
      <span className="min-w-0 flex-1">{efeito.rotulo}</span>
      {efeito.antes !== undefined ? (
        <>
          <span className="numero text-tinta-secundaria">{efeito.antes}</span>
          <span aria-hidden="true" className="text-tinta-apagada">
            →
          </span>
          <span className="sr-only">passou para</span>
        </>
      ) : null}
      <span className="numero font-semibold">{efeito.depois}</span>
    </p>
  );
}
